import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { COMETCHAT_CONSTANTS } from '../AppConstants';

/**
 * A call's server-side status ("initiated", "ongoing", "ended", "cancelled", …), read from
 * `GET /calls/:sessionId` the same way Android's push SDK checks a ring is still live.
 *
 * Why web needs it: a ringing tab only hears about the call through the Chat SDK's call
 * messages. When the caller is still in the prepared room, the Calls SDK ends the call through
 * the room instead of `PUT /calls/:id`, the server marks it ended and pushes mobile — but sends no
 * "cancelled" call message, so the tab kept ringing. The web SDK has no public call-state API,
 * hence the request here. Null when the status can't be read (offline, no session): keep ringing.
 */
export async function fetchCallStatus(sessionId: string): Promise<string | null> {
  const token = CometChatCalls.getUserAuthToken();
  if (!token) return null;
  const { APP_ID, REGION, IS_STAGING } = COMETCHAT_CONSTANTS;
  const domain = IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io';
  const url = `https://${APP_ID}.call-${REGION.toLowerCase()}.${domain}/v3.0/calls/${encodeURIComponent(sessionId)}`;
  try {
    const response = await fetch(url, {
      headers: { accept: 'application/json', appid: APP_ID, authtoken: token },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { data?: { status?: string }[] | { status?: string } };
    const call = Array.isArray(body.data) ? body.data[0] : body.data;
    return call?.status ?? null;
  } catch {
    return null;
  }
}

/**
 * What the caller's ringing screen says when the callee ends the call and says why
 * (ENG-40329) — the same words as Android and iOS. Null for a status that is not
 * the caller's to receive (`cancelled` / `unanswered` go to the callee): keep the generic end.
 */
export function outgoingEndText(status: string | null | undefined): string | null {
  switch (status) {
    case 'rejected':
      return 'Declined';
    case 'busy':
      return 'Busy';
    default:
      return null;
  }
}

/**
 * The caller's end text after the room closed: the close carries no reason, so only the server
 * knows why the call ended. Asks it, for at most `timeoutMs`, and falls back to "Call ended".
 */
export async function outgoingEndTextFromServer(sessionId: string, timeoutMs = 1500): Promise<string> {
  const status = await Promise.race([
    fetchCallStatus(sessionId),
    new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs)),
  ]);
  return outgoingEndText(status) ?? 'Call ended';
}

/**
 * Whether a call the caller is still ringing on is over on the server, and what to say.
 *
 * The caller has no reliable push for this: the callee's end is announced into the room, which
 * the caller never receives, and no call message arrives either. The server is the one place
 * that does know — it records `rejected` / `busy` as soon as the callee declines (ENG-40329) —
 * so the caller polls it while ringing, exactly as the callee already does.
 *
 * `null` means keep ringing: still `initiated`, or the status could not be read (offline, a
 * failed request), which must never end a live call.
 */
export function outgoingRingIsOver(status: string | null): string | null {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === 'initiated' || s === 'ongoing') return null;
  return outgoingEndText(s) ?? 'Call ended';
}

/**
 * Whether a ringing call should stop ringing here. A 1:1 call stops on anything but
 * "initiated": ended, cancelled, or answered on another of your devices ("ongoing"). A group call
 * keeps ringing while it's going on (others may be in it); it stops once it's over.
 */
export function ringIsOver(status: string | null, isGroup: boolean): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  if (isGroup) return ['ended', 'cancelled', 'unanswered', 'rejected'].includes(s);
  return s !== 'initiated';
}
